<script lang="ts" setup>
import { computed } from 'vue'
import { useDevMode } from '@/composables/useDevMode'
import { useAdminViewMode } from '@/composables/useAdminViewMode'
import { useJwtStore } from '@/stores'
import { useRoute } from 'vue-router'

const route = useRoute()
const jwtStore = useJwtStore()
const { adminViewMode, toggle: toggleAdminView } = useAdminViewMode()
const {
  devMode,
  simulatedRole,
  isPanelOpen,
  panelPosition,
  isDebugOverlayVisible,
  toggle: toggleDevMode,
  togglePanel,
  closePanel,
  setPanelPosition,
  setSimulatedRole,
  toggleDebugOverlay,
  performanceMetrics,
  resetMetrics
} = useDevMode()

const jwt = computed(() => jwtStore.getJwtContent)
const actualRole = computed(() => jwt.value?.role || 'unknown')

const positions = [
  { id: 'bottom-right', label: 'BR', icon: 'mdi-arrow-bottom-right' },
  { id: 'bottom-left', label: 'BL', icon: 'mdi-arrow-bottom-left' },
  { id: 'top-right', label: 'TR', icon: 'mdi-arrow-top-right' },
  { id: 'top-left', label: 'TL', icon: 'mdi-arrow-top-left' }
] as const

const roles = [
  { id: null, label: 'Actual', color: 'default' },
  { id: 'admin', label: 'Admin', color: 'purple' },
  { id: 'reseller', label: 'Reseller', color: 'teal' },
  { id: 'customer', label: 'Customer', color: 'cyan' }
] as const

const getPositionClass = computed(() => `position-${panelPosition.value}`)
</script>

<template>
  <Teleport to="body">
    <!-- Floating Toggle Button -->
    <button
      v-if="devMode && !isPanelOpen"
      class="dev-toggle-btn"
      :class="getPositionClass"
      @click="togglePanel"
      aria-label="Dev Panel öffnen"
    >
      <v-icon icon="mdi-code-tags" size="20" />
    </button>

    <!-- Dev Panel -->
    <Transition name="panel">
      <div
        v-if="devMode && isPanelOpen"
        class="dev-panel"
        :class="getPositionClass"
      >
        <header class="panel-header">
          <div class="header-title">
            <v-icon icon="mdi-code-tags" size="18" />
            <span>Dev Panel</span>
          </div>
          <div class="header-actions">
            <div class="position-selector">
              <button
                v-for="pos in positions"
                :key="pos.id"
                class="position-btn"
                :class="{ active: panelPosition === pos.id }"
                @click="setPanelPosition(pos.id)"
                :aria-label="`Position: ${pos.label}`"
              >
                <v-icon :icon="pos.icon" size="12" />
              </button>
            </div>
            <button class="close-btn" @click="closePanel" aria-label="Schließen">
              <v-icon icon="mdi-close" size="16" />
            </button>
          </div>
        </header>

        <div class="panel-body">
          <!-- Current State -->
          <section class="panel-section">
            <h3 class="section-title">Status</h3>
            <div class="status-grid">
              <div class="status-item">
                <span class="status-label">Route</span>
                <code class="status-value">{{ route.name }}</code>
              </div>
              <div class="status-item">
                <span class="status-label">Actual Role</span>
                <span class="status-value role-badge" :class="`role-${actualRole}`">
                  {{ actualRole }}
                </span>
              </div>
              <div class="status-item">
                <span class="status-label">Simulated</span>
                <span class="status-value role-badge" :class="simulatedRole ? `role-${simulatedRole}` : 'role-none'">
                  {{ simulatedRole || 'None' }}
                </span>
              </div>
              <div class="status-item">
                <span class="status-label">Admin View</span>
                <span class="status-value" :class="adminViewMode ? 'text-success' : 'text-muted'">
                  {{ adminViewMode ? 'ON' : 'OFF' }}
                </span>
              </div>
            </div>
          </section>

          <!-- Role Simulation -->
          <section class="panel-section">
            <h3 class="section-title">Role Simulation</h3>
            <div class="role-buttons">
              <button
                v-for="role in roles"
                :key="role.id ?? 'null'"
                class="role-btn"
                :class="[
                  `role-${role.color}`,
                  { active: simulatedRole === role.id }
                ]"
                @click="setSimulatedRole(role.id)"
              >
                {{ role.label }}
              </button>
            </div>
          </section>

          <!-- Quick Toggles -->
          <section class="panel-section">
            <h3 class="section-title">Toggles</h3>
            <div class="toggle-list">
              <button class="toggle-btn" @click="toggleAdminView">
                <span class="toggle-label">Admin View</span>
                <span class="toggle-indicator" :class="{ active: adminViewMode }">
                  {{ adminViewMode ? 'ON' : 'OFF' }}
                </span>
              </button>
              <button class="toggle-btn" @click="toggleDebugOverlay">
                <span class="toggle-label">Debug Overlay</span>
                <span class="toggle-indicator" :class="{ active: isDebugOverlayVisible }">
                  {{ isDebugOverlayVisible ? 'ON' : 'OFF' }}
                </span>
              </button>
              <button class="toggle-btn danger" @click="toggleDevMode">
                <span class="toggle-label">Dev Mode</span>
                <span class="toggle-indicator active">ON</span>
              </button>
            </div>
          </section>

          <!-- Performance -->
          <section class="panel-section">
            <h3 class="section-title">
              Performance
              <button class="reset-btn" @click="resetMetrics" aria-label="Reset">
                <v-icon icon="mdi-refresh" size="12" />
              </button>
            </h3>
            <div class="metrics-grid">
              <div class="metric">
                <span class="metric-value">{{ performanceMetrics.renderCount }}</span>
                <span class="metric-label">Renders</span>
              </div>
              <div class="metric">
                <span class="metric-value">{{ performanceMetrics.lastRenderTime.toFixed(1) }}ms</span>
                <span class="metric-label">Last</span>
              </div>
              <div class="metric">
                <span class="metric-value">{{ performanceMetrics.avgRenderTime.toFixed(1) }}ms</span>
                <span class="metric-label">Avg</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.dev-toggle-btn {
  position: fixed;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(156, 39, 176, 0.9);
  border: 2px solid rgba(206, 147, 216, 0.5);
  border-radius: 12px;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(156, 39, 176, 0.4);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 24px rgba(156, 39, 176, 0.5);
  }

  &.position-bottom-right {
    bottom: 20px;
    right: 20px;
  }

  &.position-bottom-left {
    bottom: 20px;
    left: 20px;
  }

  &.position-top-right {
    top: 80px;
    right: 20px;
  }

  &.position-top-left {
    top: 80px;
    left: 20px;
  }
}

.dev-panel {
  position: fixed;
  z-index: 9999;
  width: 320px;
  max-height: 80vh;
  background: rgba(20, 10, 30, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(156, 39, 176, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &.position-bottom-right {
    bottom: 20px;
    right: 20px;
  }

  &.position-bottom-left {
    bottom: 20px;
    left: 20px;
  }

  &.position-top-right {
    top: 80px;
    right: 20px;
  }

  &.position-top-left {
    top: 80px;
    left: 20px;
  }
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: rgba(156, 39, 176, 0.15);
  border-bottom: 1px solid rgba(156, 39, 176, 0.2);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #CE93D8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.position-selector {
  display: flex;
  gap: 2px;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px;
  border-radius: 6px;
}

.position-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: rgba(206, 147, 216, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: rgba(206, 147, 216, 0.8);
  }

  &.active {
    background: rgba(156, 39, 176, 0.3);
    color: #CE93D8;
  }
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 82, 82, 0.2);
    border-color: rgba(255, 82, 82, 0.3);
    color: #FF5252;
  }
}

.panel-body {
  padding: 12px;
  overflow-y: auto;
  flex: 1;
}

.panel-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(206, 147, 216, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 10px;
}

.reset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  background: transparent;
  border: none;
  color: rgba(206, 147, 216, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #CE93D8;
  }
}

.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.status-label {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}

.status-value {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: monospace;

  &.text-success {
    color: #4CAF50;
  }

  &.text-muted {
    color: rgba(255, 255, 255, 0.4);
  }
}

.role-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;

  &.role-admin {
    background: rgba(156, 39, 176, 0.2);
    color: #CE93D8;
  }

  &.role-reseller {
    background: rgba(31, 226, 212, 0.2);
    color: #1FE2D4;
  }

  &.role-customer {
    background: rgba(0, 176, 255, 0.2);
    color: #00B0FF;
  }

  &.role-unknown,
  &.role-none {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
  }
}

.role-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.role-btn {
  flex: 1;
  min-width: 60px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &.active {
    border-width: 2px;
  }

  &.role-default.active {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &.role-purple.active {
    background: rgba(156, 39, 176, 0.2);
    border-color: rgba(156, 39, 176, 0.5);
    color: #CE93D8;
  }

  &.role-teal.active {
    background: rgba(31, 226, 212, 0.2);
    border-color: rgba(31, 226, 212, 0.5);
    color: #1FE2D4;
  }

  &.role-cyan.active {
    background: rgba(0, 176, 255, 0.2);
    border-color: rgba(0, 176, 255, 0.5);
    color: #00B0FF;
  }
}

.toggle-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  &.danger:hover {
    background: rgba(255, 82, 82, 0.1);
    border-color: rgba(255, 82, 82, 0.2);
  }
}

.toggle-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
}

.toggle-indicator {
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);

  &.active {
    background: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
  }
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.metric-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-family: monospace;
}

.metric-label {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
}

/* Panel transition */
.panel-enter-active,
.panel-leave-active {
  transition: all 0.25s ease;
}

.panel-enter-from,
.panel-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

@media (max-width: 400px) {
  .dev-panel {
    width: calc(100vw - 40px);
    max-width: 320px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dev-toggle-btn,
  .position-btn,
  .close-btn,
  .role-btn,
  .toggle-btn,
  .reset-btn {
    transition: none;
  }

  .panel-enter-active,
  .panel-leave-active {
    transition: opacity 0.1s ease;
  }

  .panel-enter-from,
  .panel-leave-to {
    transform: none;
  }
}
</style>
